// client/src/pages/Login.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/axios';
import { Loader2, Eye, EyeOff } from "lucide-react";

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await api.post('/login', { username, password });

      localStorage.setItem('token', res.data.token);
      localStorage.setItem('role_id', res.data.user.role_id);
      localStorage.setItem('username', username);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-hidden">
      {/* Animated Rotating Curved Lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute border border-custom-orange/20 rounded-full"
            style={{
              width: `${280 + i * 60}px`,
              height: `${280 + i * 60}px`,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              animation: `rotate ${12 + i * 1.5}s linear infinite`,
              animationDelay: `-${i * 0.8}s`,
              opacity: 0.6 - i * 0.05,
            }}
          />
        ))}
      </div>

      {/* Subtle Background Glows */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(249,115,22,0.08),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(249,115,22,0.06),transparent_60%)]"></div>

      {/* Login Card */}
      <div className="relative z-10 bg-white/95 backdrop-blur-xl border border-gray-200 rounded-3xl shadow-2xl w-full max-w-md p-10">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img
            src="/Jimmac-Logo.png"
            alt="Jimmac Logo"
            className="h-16 w-auto mb-4"
            onError={(e) => { e.target.src = 'https://via.placeholder.com/180x60/111827/ffffff?text=JIMMAC'; }}
          />
          <h3 className="text-1xl font-regular tracking-tight text-gray-900" >Work Management System</h3>        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-2xl border border-red-200 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-5 py-4 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-custom-orange focus:border-custom-orange outline-none transition"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-5 py-4 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-custom-orange focus:border-custom-orange outline-none transition"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-4 rounded-2xl text-lg font-semibold transition-all flex items-center justify-center gap-3 shadow-lg ${
              loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-custom-orange hover:bg-orange-600 text-white'
            }`}
          >
            {loading ? (
              <>
                <Loader2 size={22} className="animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Forgot Password */}
        <div className="mt-6 text-center">
          <Link
            to="/forgot-password"
            className="text-custom-orange hover:text-orange-600 font-medium text-sm transition"
          >
            Forgot your password?
          </Link>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 text-gray-400 text-xs">
        © 2026 Jimmac • Secure Access
      </div>

      {/* CSS Animation for Rotating Lines */}
      <style jsx>{`
        @keyframes rotate {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Login;